import { inject, Injector, Service } from '@angular/core';
import { CurrentUser } from '@core/user/current-user';
import { WebSocketActionEnum } from '@shared/enums/websocket';
import { WebSocketMessage, CommandArgs, EquipmentOutbound } from '@shared/models/websocket';
import { LiveSession } from '../live/live-session';
import { Medinbox } from '../equipment/medinbox';
import { WebSocketClient } from './web-socket-client';

export interface EquipmentCommandTarget {
  /** Defaults to the connected box. A key given as undefined is sent as is. */
  equipmentId?: string | null;
  /** Defaults to the signed-in user. */
  sourceId?: string;
}

export interface LiveEquipmentCommandTarget extends EquipmentCommandTarget {
  /** Defaults to the current live code, or null outside a live. */
  liveCode?: string | null;
}

/** The only place that builds the messages sent to the box. */
@Service()
export class EquipmentCommand {
  // Read lazily: Medinbox and LiveSession themselves send box commands.
  private readonly injector = inject(Injector);
  private readonly webSocketClient = inject(WebSocketClient);
  private readonly userService = inject(CurrentUser);

  /** Sends a command tagged with a live code. */
  send<A extends keyof EquipmentOutbound>(action: A, ...[params, target = {}]: CommandArgs<EquipmentOutbound[A], LiveEquipmentCommandTarget>): Promise<void> {
    const liveCode = 'liveCode' in target ? target.liveCode : (this.injector.get(LiveSession).currentLive()?.code ?? null);
    return this.webSocketClient.sendMedinboxMessage({ ...this.build(action, params, target), liveCode } as WebSocketMessage);
  }

  /** Sends a command that carries no live code at all. */
  sendOutsideLive<A extends keyof EquipmentOutbound>(
    action: A,
    ...[params, target = {}]: CommandArgs<EquipmentOutbound[A], EquipmentCommandTarget>
  ): Promise<void> {
    return this.webSocketClient.sendMedinboxMessage(this.build(action, params, target));
  }

  private build(action: WebSocketActionEnum, params: unknown, target: EquipmentCommandTarget): WebSocketMessage {
    return {
      audience: 'EQUIPMENT',
      sourceId: target.sourceId ?? this.userService.currentUserId(),
      destinationId: 'equipmentId' in target ? target.equipmentId : this.injector.get(Medinbox).medinboxId(),
      message: params === undefined ? { action } : { action, params },
    } as WebSocketMessage;
  }
}
